import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { ServiceRouter } from 'src/app/services/core/http-service-router.service';
import { Config } from 'src/app/config/config';
import { Router, ActivatedRoute } from '@angular/router';
import { METHOD_TYPE } from '../../../constants/service.constants';
import { Environment } from '../../../config/environment';
import { EditorConfig } from '../../../config/editor-config';
import { ModalService } from '../../../services/core/modal.service';
import { MessagingService } from 'src/app/services/messaging/messaging.service';
import { AppUtil } from '../../../utils/app.util';
import { AnalyticsBean } from 'src/app/beans/analytics/analytics-module.bean';
import { Subscription } from 'rxjs';
import { EheAnalyticsDirective } from '../../shared/ehe-analytics/ehe-analytics.directive';
import { Editor, Toolbar } from 'ngx-editor';

@Component({
	selector: 'app-compose',
	templateUrl: './compose.component.html',
	styleUrls: ['./compose.component.scss'],
})
/**
 * Compose message component
 */
export class ComposeComponent implements OnInit, OnDestroy {
	/** compose message form */
	composeMessageForm: FormGroup;
	/**
	 * Holds message body data to be sent
	 */
	body: any;
	/** flag to denote if the page comes from referral module  */
	fromReferral: boolean = false;
	/** flag to denote if the page comes from Confirmed Appointment current referral module  */
	fromConfirmedAppointmentsReferral: boolean = false;
	/** Holds config related to Text Editor */
	editorConfig = EditorConfig.config;
	/** holds the redirect page, if user navigates to compose from inbox, user is routed back to inbox again */
	redirect: any;
	/** flag to denote if the browser is IE */
	isIE: boolean = false;
	/** contains data related to Analytics */
	analyticsData: AnalyticsBean;
	/** contains list of subscriptions */
	subscriptions: Subscription[] = [];
	editor: Editor;

	html: '';

	toolbar: Toolbar = [
		['bold', 'italic'],
		['underline', 'strike'],
		['code', 'blockquote'],
		['ordered_list', 'bullet_list'],
		[{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
		['link', 'image'],
		['text_color', 'background_color'],
		['align_left', 'align_center', 'align_right', 'align_justify'],
	];
	/**
	 * Compose component constructor
	 * @param router Angular router
	 * @param serviceRouter Service Router
	 * @param config URL configuration
	 * @param spinner Spinner service
	 * @param environment Environment
	 * @param routeParams Activated Route
	 * @param modalService Modal Service
	 * @param messageService Message Service
	 * @param eheAnalyticsDirective Analytics Directive
	 */
	constructor(
		public router: Router,
		private serviceRouter: ServiceRouter,
		private config: Config,
		private spinner: SpinnerComponent,
		private environment: Environment,
		private routeParams: ActivatedRoute,
		private modalService: ModalService,
		private messageService: MessagingService,
		private eheAnalyticsDirective: EheAnalyticsDirective
	) {
		// Captures the previous route

		this.subscriptions.push(
			this.routeParams.queryParams.subscribe(params => {
				this.fromReferral = params.fromReferral;
				if (params.params) {
					try {
						const param = JSON.parse(atob(params.params));
						this.fromConfirmedAppointmentsReferral = param.fromConfirmedAppointmentReferral;
					} catch (Exception) {
						console.error(`[${ComposeComponent.name}]`, 'Error occurred while decoded the base64 param', Exception);
					}
				}
				if (params.redirect) {
					try {
						this.redirect = JSON.parse(atob(params.redirect));
					} catch (Exception) {
						this.redirect = '';
					}
				} else {
					// When coming from megamenu - dashboard - redirecting to inbox
					this.redirect = '/messaging/mailbox/inbox';
				}
			})
		);
		this.analyticsData = new AnalyticsBean();
		this.analyticsData.componentName = 'Compose';
		this.analyticsData.pageUrl = '/messaging/compose';
		this.editor = new Editor();
	}

	/**
	 * On component initialization
	 */
	ngOnInit() {
		this.editor = new Editor();
		this.initializeComposeMessageForm();
		// Checks if browser is IE
		if (AppUtil.isIE()) {
			this.isIE = true;
		}
	}

	/**
	 * Contains initialization of compose message form
	 * Form elements are toReceiverType, msgSubject, msgBody
	 */
	initializeComposeMessageForm() {
		this.composeMessageForm = new FormGroup({
			toReceiverType: new FormControl(
				this.fromReferral || this.fromConfirmedAppointmentsReferral ? '5' : '',
				Validators.compose([Validators.required])
			),
			msgSubject: new FormControl('', Validators.compose([Validators.required])),
			msgBody: new FormControl('', Validators.compose([Validators.required])),
		});
	}

	/**
	 * Function to post a message to service, when the form is valid.
	 */
	sendMessage() {
		this.analyticsData.id = 'Send message';
		this.analyticsData.redirectedTo = '/messaging/compose';
		this.analyticsData.placement = 'Messaging compose';
		this.body = '';
		// For IE, the body is not captured properly using app-ngx-editor plugin, hence handling it differently
		if (this.isIE) {
			try {
				const temp = document.createElement('div');
				temp.innerHTML = document.getElementById('msgBody').innerHTML;
				this.body = temp.getElementsByClassName('ngx-editor-textarea')[0].innerHTML;
			} catch (Exception) {
				console.error(`[${ComposeComponent.name}][${this.sendMessage.name}]`, Exception);
			}
		}
		if (this.isIE && this.body.length > 0) {
			this.composeMessageForm.controls['msgBody'].setErrors(null);
		}
		if (this.composeMessageForm.valid) {
			this.spinner.startLoader();
			const msgData = this.composeMessageForm.getRawValue();
			if (this.isIE) {
				msgData.msgBody = this.body;
			}
			this.subscriptions.push(
				this.serviceRouter
					.makeRequest(
						this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
						this.config.url.messagingApi.getMessages,
						METHOD_TYPE.SECURED_POST,
						msgData
					)
					.subscribe(
						_ => {
							this.spinner.stopLoader();
							const messageCount = this.messageService.getCurrentMessageCount().getValue();
							messageCount['sent'] += 1;
							// Setting message count, whose value is listened by html - messageCount - for inbox and sent folder
							this.messageService.setCurrentMessageCount(messageCount);
							this.modalService.openInfoModal(
								this.config.content.errorTitle.ERRTIT001,
								this.config.content.errorType.ERRTYP001,
								this.config.content.messaging.MSGMSG001,
								'OK'
							);
							this.analyticsData.id = this.config.content.messaging.MSGMSG001;
							this.analyticsData.redirectedTo = '/messaging/compose';
							this.analyticsData.placement = 'Compose mail modal';
							this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
							if (this.fromReferral) {
								this.router.navigate(['wellness/referral']);
							} else if (this.fromConfirmedAppointmentsReferral) {
								this.router.navigate(['wellness/referral/current/confirmed/appointments']);
							} else {
								this.router.navigate([`${this.redirect}`]);
							}
						},
						_ => {
							this.spinner.stopLoader();
							this.cancelSendMessage();
						}
					)
			);
		}
	}

	/**
	 * When cancel is pressed in send message page, the form is reinitialized and goes back to page from which user navigates to send message form
	 */
	cancelSendMessage() {
		this.analyticsData.id = 'Cancel send message';
		this.analyticsData.redirectedTo = '/messaging/compose';
		this.analyticsData.placement = 'Messaging compose';
		this.initializeComposeMessageForm();
		if (this.fromReferral) {
			this.router.navigate(['wellness/referral']);
		} else if (this.fromConfirmedAppointmentsReferral) {
			this.router.navigate(['wellness/referral/current/confirmed/appointments']);
		} else {
			this.router.navigate([`${this.redirect}`]);
		}
	}

	/**
	 * Cleans the component
	 */
	ngOnDestroy() {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
		this.editor.destroy();
	}
}
